import { useRef, useState, useMemo } from "react";
import { toast } from "sonner";
import { Sparkles, Loader2, ArrowRight, PlayCircle, RefreshCw, Smartphone, CheckCircle2, ListChecks, Pencil, Save, Undo2, SkipForward } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { ThreadLayer, CONN_COLORS } from "@/components/pms/ThreadLayer";
import CursorGuide from "@/components/pms/CursorGuide";
import HotelMapping from "@/components/pms/HotelMapping";
import EmptyPoolCTA from "@/components/pms/EmptyPoolCTA";

const PencilIcon = () => <Pencil size={11} className="inline" />;

export default function Phase2Mapping(props) {
  const isHotel = (props.data?.property?.category || "homestay").toLowerCase() === "hotel";
  if (isHotel) return <HotelMapping {...props} />;
  return <HomestayMapping {...props} />;
}

function HomestayMapping({ data, reload, onDone }) {
  const { property, units, mappings, can_edit_setup } = data;
  const ref = useRef(null);
  const [selUnit, setSelUnit] = useState(null);
  const [ai, setAi] = useState([]);
  const [busyAi, setBusyAi] = useState(false);
  const [busy, setBusy] = useState(false);
  const [idCells, setIdCells] = useState({});
  const [customId, setCustomId] = useState("");
  const [guideN, setGuideN] = useState(0);
  const [adminEdit, setAdminEdit] = useState(false);
  const [stagedNewMappings, setStagedNewMappings] = useState([]); // [{unit_id, listing_id, ai}]
  const [stagedDeletedMappings, setStagedDeletedMappings] = useState(new Set()); // server mapping ids

  // Read-only after activation UNLESS admin toggles Edit mode
  const readonly = property.status === "active" && !adminEdit;

  const stagedCount = stagedNewMappings.length + stagedDeletedMappings.size;
  const dirty = stagedCount > 0;

  const displayMappings = useMemo(() => mappings.filter((m) => !stagedDeletedMappings.has(m.id)), [mappings, stagedDeletedMappings]);
  const isPendingMapped = (lid) => displayMappings.some((m) => m.listing_id === lid) || stagedNewMappings.some((s) => s.listing_id === lid);

  const rows = data.pool.filter((p) => p.status === "available" || (p.status === "assigned" && p.property_id === property.id));
  const unitName = (id) => units.find((u) => u.id === id)?.name || "?";
  const cellValue = (lid) => idCells[lid] ?? lid;

  const stageNewMapping = (unitId, listingId, isAi) => {
    if (isPendingMapped(listingId)) return toast.error("This listing is already mapped");
    setStagedNewMappings((s) => [...s, { unit_id: unitId, listing_id: listingId, ai: !!isAi }]);
    setAi((s) => s.filter((x) => !(x.unit_id === unitId && x.listing_id === listingId)));
    toast.success(`${listingId} → Unit ${unitName(unitId)} · pending, click Save Changes`);
  };

  const removeStagedNew = (unitId, listingId) => {
    setStagedNewMappings((s) => s.filter((m) => !(m.unit_id === unitId && m.listing_id === listingId)));
  };

  const stageDeleteMapping = (mid) => {
    setStagedDeletedMappings((s) => new Set(s).add(mid));
  };

  const undoDelete = (mid) => {
    setStagedDeletedMappings((s) => { const n = new Set(s); n.delete(mid); return n; });
  };

  const discardAll = () => {
    setStagedNewMappings([]); setStagedDeletedMappings(new Set()); setAi([]);
    toast.info("All staged mapping edits discarded");
  };

  const saveAll = async () => {
    if (!dirty) return;
    setBusy(true);
    try {
      for (const mid of stagedDeletedMappings) {
        await api.delete(`/pms/mappings/${mid}`);
      }
      for (const m of stagedNewMappings) {
        await api.post(`/pms/properties/${property.id}/mappings`, { unit_id: m.unit_id, listing_id: m.listing_id, ai: m.ai });
      }
      toast.success(`${stagedCount} mapping change${stagedCount === 1 ? "" : "s"} saved · broadcast to every OTA`);
      setStagedNewMappings([]); setStagedDeletedMappings(new Set());
      await reload();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const clickRight = (rowListingId) => {
    if (readonly) return;
    if (!selUnit) return toast.info("Tap a unit on the left first");
    stageNewMapping(selUnit, cellValue(rowListingId).trim(), false);
    setSelUnit(null);
  };

  const linkCustom = () => {
    if (readonly) return;
    if (!selUnit) return toast.info("Tap a unit on the left first");
    if (!customId.trim()) return toast.error("Type or paste a listing ID first");
    stageNewMapping(selUnit, customId.trim(), false);
    setSelUnit(null); setCustomId("");
  };

  const suggest = async () => {
    setBusyAi(true);
    try {
      const { data: r } = await api.post("/pms/ai/suggest", { property_id: property.id, phase: "mapping" });
      const fresh = r.suggestions.filter((s) => !isPendingMapped(s.listing_id));
      setAi(fresh);
      toast[fresh.length ? "success" : "info"](fresh.length ? `AI proposed ${fresh.length} mapping(s) — dashed threads await your confirmation` : "No AI matches found for this property");
    } catch (e) { toast.error(errMsg(e)); }
    setBusyAi(false);
  };

  const complete = async (skip = false) => {
    if (dirty) {
      await saveAll();
      if (stagedNewMappings.length || stagedDeletedMappings.size) return;
    }
    if (!skip && displayMappings.length + stagedNewMappings.length === 0) return toast.error("Map at least one listing before billing, or click Skip for now");
    setBusy(true);
    try {
      await api.post(`/pms/properties/${property.id}/setup-complete`, skip ? { skip_mapping: true } : {});
      toast.success(skip ? "Mapping skipped — continue to billing" : "Setup complete — invoice generated");
      await reload();
      onDone();
    } catch (e) { toast.error(errMsg(e)); }
    setBusy(false);
  };

  const skip = () => complete(true);
  const isNewHost = !readonly && data.pool.length === 0 && displayMappings.length === 0 && stagedNewMappings.length === 0;

  const threadLinks = [
    ...displayMappings.map((m, i) => ({ key: m.id, from: `u-${m.unit_id}`, to: `l-${m.listing_id}`,
      color: CONN_COLORS[i % CONN_COLORS.length], dashed: false,
      onRemove: readonly ? null : () => stageDeleteMapping(m.id) })),
    ...stagedNewMappings.map((s, i) => ({ key: `new-${s.unit_id}-${s.listing_id}`, from: `u-${s.unit_id}`, to: `l-${s.listing_id}`,
      color: "#10B981", dashed: true, onRemove: () => removeStagedNew(s.unit_id, s.listing_id) })),
    ...ai.map((s, i) => ({ key: `ai-${i}`, from: `u-${s.unit_id}`, to: `l-${s.listing_id}`, color: "#4F46E5", dashed: true, onAccept: () => stageNewMapping(s.unit_id, s.listing_id, true), onDismiss: () => setAi((x) => x.filter((_, j) => j !== i)) })),
  ];

  return (
    <div className="reveal">
      {property.status === "active" && can_edit_setup && (
        <div className="mb-4 flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-xl px-4 py-2.5 flex-wrap">
          {adminEdit ? (
            <>
              <span className={`text-[11px] font-semibold flex items-center gap-1.5 ${dirty ? "text-emerald-700" : "text-slate-600"}`}>
                <PencilIcon /> {dirty ? `${stagedCount} mapping change${stagedCount === 1 ? "" : "s"} pending — click Save Changes to broadcast.` : "Admin edit mode — stage all your changes, then click Save Changes."}
              </span>
              <div className="ml-auto flex items-center gap-2">
                <button data-testid="phase2-save-changes-button" onClick={saveAll} disabled={busy || !dirty}
                  className="btn-brand rounded-full px-4 py-1.5 text-[11px] font-semibold flex items-center gap-1.5 disabled:opacity-50">
                  {busy ? <Loader2 className="animate-spin" size={11} /> : <Save size={11} />} Save {dirty ? `${stagedCount} change${stagedCount === 1 ? "" : "s"}` : "changes"}
                </button>
                <button data-testid="phase2-discard-button" onClick={discardAll} disabled={busy || !dirty}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-slate-500 disabled:opacity-40 font-semibold flex items-center gap-1.5">
                  <Undo2 size={11} /> Discard
                </button>
                <button data-testid="phase2-lock-button" onClick={() => { if (dirty) { toast.error("Save or discard your pending edits first"); return; } setAdminEdit(false); toast.success("Mappings locked back."); }}
                  className="text-[11px] px-3 py-1.5 rounded-full border border-slate-300 text-slate-600 hover:border-slate-500 font-semibold">Done editing</button>
              </div>
            </>
          ) : (
            <>
              <span className="text-[11px] text-slate-500">PMS is live — mappings are read-only for safety.</span>
              <button data-testid="phase2-unlock-button" onClick={() => { setAdminEdit(true); toast.info("Editing enabled — you can now unmap or add listings."); }}
                className="ml-auto text-[11px] px-3 py-1 rounded-full border border-[#4F46E5] brand-pink hover:bg-[#EEF2FF] font-semibold flex items-center gap-1.5"><PencilIcon /> Edit mappings (admin)</button>
            </>
          )}
        </div>
      )}
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">Phase 2 — map Atiti listings onto units</h2>
          <p className="text-xs text-slate-500 mt-1">Tap a unit, then tap a listing row (or type/paste an ID into a cell). Multiple listings can point at one unit. Dashed threads are AI proposals — accept to confirm.</p>
        </div>
        <div className="shrink-0 flex items-center gap-2 flex-wrap">
          {!readonly && !isNewHost && (
            <button data-testid="homestay-mapping-skip-button" onClick={skip} disabled={busy}
              className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-indigo-500 hover:text-indigo-600 transition-colors disabled:opacity-50">
              <SkipForward size={13} /> Skip for now
            </button>
          )}
          <button data-testid="guide-replay-phase2" onClick={() => setGuideN((n) => n + 1)}
            className="flex items-center gap-1.5 text-xs font-semibold px-4 py-2 rounded-full border border-slate-300 text-slate-500 hover:border-slate-500 hover:text-slate-700 transition-colors">
            <PlayCircle size={13} /> Show me how
          </button>
          <button data-testid="ai-suggest-mapping-button" onClick={suggest} disabled={busyAi}
            className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded-full border border-[#4F46E5] brand-pink hover:bg-[#EEF2FF] transition-colors">
            {busyAi ? <Loader2 className="animate-spin" size={13} /> : <Sparkles size={13} />} AI suggest matches
          </button>
        </div>
      </div>

      {isNewHost && (
        <div className="mb-6">
          <EmptyPoolCTA pid={property.id} onRefresh={reload} onSkip={async () => { await reload(); onDone(); }} />
        </div>
      )}

      <div ref={ref} className="relative grid grid-cols-[1fr_120px_1.4fr] gap-0">
        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2.5 self-start">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Units</p>
          {units.map((u) => (
            <div key={u.id} data-conn-id={`u-${u.id}`} data-testid={`map-unit-${u.name}`} onClick={() => setSelUnit(selUnit === u.id ? null : u.id)}
              className={`conn-row flex items-center justify-between border border-slate-200 rounded-xl px-4 py-3 ${selUnit === u.id ? "selected" : ""}`}>
              <div className="flex items-center gap-3 min-w-0">
                <span className="h-8 px-2.5 min-w-[32px] max-w-[160px] rounded-lg bg-slate-900 text-white font-display font-bold text-xs flex items-center justify-center truncate">{u.name}</span>
                <span className="text-xs text-slate-500 shrink-0">{displayMappings.filter((m) => m.unit_id === u.id).length + stagedNewMappings.filter((s) => s.unit_id === u.id).length} listing(s)</span>
              </div>
              <span className="w-2.5 h-2.5 rounded-full bg-slate-300" />
            </div>
          ))}
        </div>

        <div />

        <div className="bg-white rounded-2xl border border-slate-200 p-5 space-y-2.5 self-start">
          <p className="text-[11px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Atiti listing pool — editable ID cells</p>
          {rows.length === 0 ? (
            <EmptyPoolEducator onRefresh={reload} />
          ) : (
            rows.map((l) => {
            const mapped = isPendingMapped(l.listing_id);
            return (
              <div key={l.listing_id} data-conn-id={`l-${l.listing_id}`} data-testid={`map-listing-${l.listing_id}`} onClick={() => !mapped && clickRight(l.listing_id)}
                className={`conn-row flex items-center gap-3 border border-slate-200 rounded-xl px-4 py-2.5 ${mapped ? "bg-slate-50 cursor-default" : ""}`}>
                <span className="w-2.5 h-2.5 rounded-full bg-slate-300 shrink-0" />
                <input data-testid={`id-cell-${l.listing_id}`} value={cellValue(l.listing_id)} disabled={mapped}
                  onClick={(e) => e.stopPropagation()}
                  onChange={(e) => setIdCells({ ...idCells, [l.listing_id]: e.target.value })}
                  className="font-mono text-xs w-20 border border-slate-200 rounded-md px-1.5 py-1 bg-white focus:outline-none focus:border-[#4F46E5] disabled:bg-transparent disabled:border-transparent" />
                <div className="min-w-0">
                  <p className={`text-sm truncate ${mapped ? "text-slate-400" : "text-slate-700"}`}>{l.title}</p>
                  <p className="text-[11px] text-slate-400">{l.city} · {l.bookings} bookings</p>
                </div>
              </div>
            );
          }))}
          <div data-conn-id="l-custom" className="flex items-center gap-3 border-2 border-dashed border-slate-200 rounded-xl px-4 py-2.5">
            <span className="w-2.5 h-2.5 rounded-full bg-slate-200 shrink-0" />
            <input data-testid="custom-id-cell" value={customId} onChange={(e) => setCustomId(e.target.value)} placeholder={rows.length === 0 ? "Have a listing ID handy? Paste it here to link manually…" : "Paste a listing / hotel ID…"}
              className="font-mono text-xs flex-1 focus:outline-none bg-transparent" />
            <button data-testid="link-custom-id-button" onClick={linkCustom} className="text-xs font-semibold brand-pink hover:underline">link</button>
          </div>
        </div>

        <ThreadLayer containerRef={ref} links={threadLinks} />
        {units.length > 0 && rows.length > 0 && (
          <CursorGuide containerRef={ref} fromId={`u-${units[0].id}`} toId={`l-${rows[0].listing_id}`}
            storageKey="atiti_guide_phase2" playSignal={guideN}
            messages={["Tap a unit on the left…", "…then tap the listing it represents (or type its ID in the cell)", "Mapped! Dashed pink threads are AI suggestions — tap ✓ to accept them."]} />
        )}
      </div>

      {stagedDeletedMappings.size > 0 && (
        <div className="mt-3 flex items-center gap-2 flex-wrap">
          <span className="text-[11px] font-semibold text-slate-500">Pending removes:</span>
          {Array.from(stagedDeletedMappings).map((mid) => {
            const m = mappings.find((x) => x.id === mid);
            if (!m) return null;
            return (
              <button key={mid} data-testid={`undo-delete-mapping-${mid}`} onClick={() => undoDelete(mid)}
                className="text-[11px] px-2.5 py-1 rounded-full bg-rose-50 border border-rose-200 text-rose-700 hover:bg-rose-100 flex items-center gap-1">
                <Undo2 size={10} /> Unit {unitName(m.unit_id)} ↔ {m.listing_id}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex items-center justify-between mt-6 flex-wrap gap-3">
        <p className="text-xs text-slate-400">{selUnit ? `Unit ${unitName(selUnit)} selected — tap a listing row to link.` : `${displayMappings.length + stagedNewMappings.length} listing(s) mapped${dirty ? ` · ${stagedCount} pending` : ""}.`}</p>
        <div className="flex items-center gap-2">
          {property.status !== "active" && dirty && (
            <>
              <button data-testid="phase2-save-changes-button" onClick={saveAll} disabled={busy}
                className="rounded-full px-4 py-2.5 text-xs font-semibold border-2 border-emerald-500 text-emerald-700 hover:bg-emerald-50 flex items-center gap-1.5 disabled:opacity-50">
                {busy ? <Loader2 className="animate-spin" size={12} /> : <Save size={12} />} Save {stagedCount}
              </button>
              <button data-testid="phase2-discard-button" onClick={discardAll} disabled={busy}
                className="rounded-full px-3 py-2.5 text-xs font-semibold border border-slate-300 text-slate-600 hover:border-slate-500 flex items-center gap-1.5 disabled:opacity-40">
                <Undo2 size={12} /> Discard
              </button>
            </>
          )}
          <button data-testid="mapping-complete-button" onClick={complete} disabled={busy}
            className="btn-brand rounded-full px-6 py-3 text-sm font-semibold flex items-center gap-2">
            {busy ? <Loader2 className="animate-spin" size={16} /> : null} {dirty && property.status !== "active" ? "Save & finish setup" : "Finish setup → generate invoice"} <ArrowRight size={14} />
          </button>
        </div>
      </div>
    </div>
  );
}

function EmptyPoolEducator({ onRefresh }) {
  const [refreshing, setRefreshing] = useState(false);
  const refresh = async () => {
    setRefreshing(true);
    try {
      const { data: r } = await api.post("/pms/refresh-pool");
      if (r.added > 0) toast.success(`Pulled ${r.added} new listing${r.added > 1 ? "s" : ""} from your Atiti account`);
      else if (r.total_on_ota > 0) toast.info("All your Atiti listings are already in the pool — check the assigned ones below");
      else toast.info("Still no listings on your Atiti account yet — create one on the app first");
      await onRefresh();
    } catch (e) { toast.error(errMsg(e)); }
    setRefreshing(false);
  };
  const steps = [
    { icon: Smartphone, t: "Download the Atiti app", d: "Free on both stores. Same login you're using here." },
    { icon: ListChecks, t: "Create your homestay listings", d: "Add photos, pricing, availability — takes 2 minutes per listing." },
    { icon: CheckCircle2, t: "Come back and hit Refresh", d: "Your listings appear here automatically, ready to map onto units." },
  ];
  return (
    <div data-testid="empty-pool-educator" className="py-2">
      <div className="text-center mb-5">
        <div className="w-14 h-14 rounded-2xl bg-[#EEF2FF] border border-[#C7D2FE] flex items-center justify-center mx-auto mb-4">
          <Sparkles size={22} className="brand-pink" />
        </div>
        <h3 className="font-display font-bold text-slate-900 text-base">List on Atiti first — then map here</h3>
        <p className="text-xs text-slate-500 mt-2 leading-relaxed max-w-sm mx-auto">
          Atiti PMS pulls its listing pool from the <b>Atiti OTA app</b>. Once you list your homestay there, every listing shows up here so you can map it to a physical unit.
        </p>
      </div>

      <div className="space-y-2.5 mb-5">
        {steps.map((s, i) => {
          const Icon = s.icon;
          return (
            <div key={i} className="flex items-start gap-3 bg-slate-50 border border-slate-100 rounded-xl px-3.5 py-3">
              <div className="w-8 h-8 rounded-lg bg-white border border-slate-200 flex items-center justify-center shrink-0">
                <span className="text-[10px] font-bold text-slate-400">{i + 1}</span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-display font-bold text-slate-800 text-sm flex items-center gap-1.5"><Icon size={13} className="brand-pink" /> {s.t}</p>
                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug">{s.d}</p>
              </div>
            </div>
          );
        })}
      </div>

      <div className="flex items-center gap-2.5">
        <a data-testid="empty-pool-playstore-badge" href="https://play.google.com/store/apps/details?id=com.atititi.app" target="_blank" rel="noopener noreferrer" className="flex-1">
          <img src="https://nammayatri.in/logos/playStore.svg" alt="Get it on Google Play" className="h-11 w-full object-contain hover:scale-[1.03] transition-transform" />
        </a>
        <a data-testid="empty-pool-appstore-badge" href="https://apps.apple.com/app/id6772948860" target="_blank" rel="noopener noreferrer" className="flex-1">
          <img src="https://nammayatri.in/logos/appStore.svg" alt="Download on the App Store" className="h-11 w-full object-contain hover:scale-[1.03] transition-transform" />
        </a>
      </div>

      <button data-testid="empty-pool-refresh-button" onClick={refresh} disabled={refreshing}
        className="w-full mt-3 rounded-xl border border-slate-200 hover:border-[#4F46E5] py-2.5 text-xs font-semibold text-slate-700 hover:text-[#4F46E5] flex items-center justify-center gap-2 transition-colors disabled:opacity-50">
        {refreshing ? <Loader2 className="animate-spin" size={13} /> : <RefreshCw size={13} />} I've listed on Atiti — refresh my pool
      </button>

      <p className="text-[10px] text-slate-400 text-center mt-3 leading-relaxed">
        Already have a listing on another platform? Paste its ID in the cell below to link it manually.
      </p>
    </div>
  );
}
